import { LitElement, html, css, PropertyValues, nothing, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { ref, createRef } from "lit/directives/ref.js";
import {
  resolveColor,
  resolveSemanticSurfaceKey,
  semanticSoftSurfaceTokens,
} from "../helper/utils/color-resolver";
import { cssOnFillEnhance, ON_COLOR_FALLBACK_LIGHT } from "../helper/utils/contrast-color";
import { styleMap } from "lit/directives/style-map.js";
import { PresetController } from "../helper/controllers/preset-controller";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";

export type SkyBadgeClickEventDetail = { originalEvent: Event };
export type SkyBadgePosition = "default" | "floating" | "inline";
export type SkyBadgeShape = "circle" | "pill" | "custom";
export type SkyBadgePlacement =
  | "custom"
  | "top-right"
  | "top-left"
  | "bottom-left"
  | "bottom-right"
  | "top-center";

const SKY_BADGE_POSITIONS: readonly SkyBadgePosition[] = [
  "default",
  "floating",
  "inline",
] as const;
const SKY_BADGE_SHAPES: readonly SkyBadgeShape[] = ["circle", "pill", "custom"] as const;
const SKY_BADGE_PLACEMENTS: readonly SkyBadgePlacement[] = [
  "custom",
  "top-right",
  "top-left",
  "bottom-left",
  "bottom-right",
  "top-center",
] as const;

/**
 * @element sky-badge
 *
 * @summary Badge for status dots, counts, or icons anchored to slotted content.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/badge
 * @dependency sky-icon
 *
 * @slot - The content the badge attaches to.
 * @slot badge - Optional slot to override the badge content entirely.
 *
 * @fires {CustomEvent<SkyBadgeClickEventDetail>} badge-click - Emitted when badge activation occurs via click, Enter, or Space while `interactive` is true.
 *
 * @csspart container - Wrapper around the slotted content and the badge.
 * @csspart badge - The badge element. Use `badgeRef` for positioning (e.g. tooltip anchor).
 * @csspart icon - The icon element when `isIcon` is true.
 *
 * @Behavior
 * - Visibility is controlled by `show` and `hideWhenZero`.
 * - Numeric values above `max` render as `<max>+`.
 * - Interactive mode supports click and keyboard activation.
 * - Reduced motion mode removes badge transitions.
 *
 * @property {string} statusColor - Badge background token or CSS color.
 * @property {string} textColor - Text color token or CSS color.
 * @property {string} iconColor - Icon color token or CSS color; falls back to `textColor` when empty.
 * @property {SkyBadgePosition} position - Layout mode: `default | floating | inline`.
 * @property {string} value - Badge text/count or icon name in icon mode.
 * @property {boolean} isIcon - Renders icon mode when true.
 * @property {string} size - Badge and icon size CSS length.
 * @property {SkyBadgeShape} shape - Badge shape: `circle | pill | custom`.
 * @property {number} max - Maximum numeric count before rendering overflow label.
 * @property {boolean} hideWhenZero - Hides badge when value resolves to numeric zero.
 * @property {boolean} show - Forces badge visibility independent of value.
 * @property {string} offsetX - Horizontal offset CSS length.
 * @property {string} offsetY - Vertical offset CSS length.
 * @property {boolean} ring - Enables visual ring around the badge.
 * @property {string} ringColor - Ring color token or CSS color.
 * @property {string} ringOffset - Ring offset CSS length.
 * @property {string | null} badgeLabel - ARIA label override.
 * @property {boolean} badgeAriaHidden - Hides badge from assistive technology when true.
 * @property {boolean} interactive - Enables focus/activation and `badge-click` emission.
 * @property {SkyBadgePlacement} placement - Anchor placement preset for badge positioning.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method showBadge Forces badge visibility on.
 * @method hideBadge Forces badge visibility off.
 * @method setValue Updates badge value text or icon name.
 *
 * @example
 * ```html
 * <sky-badge value="5" statuscolor="danger" placement="top-right">
 *   <sky-icon icon="mdi:bell-outline"></sky-icon>
 * </sky-badge>
 * ```
 * ```vue
 * <template>
 *   <sky-badge value="5" statuscolor="danger" placement="top-right">
 *     <sky-icon icon="mdi:bell-outline"></sky-icon>
 *   </sky-badge>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-badge value="5" statuscolor="danger" placement="top-right">
 *       <sky-icon icon="mdi:bell-outline"></sky-icon>
 *     </sky-badge>
 *   );
 * }
 * ```
 */
@customElement("sky-badge")
export class SkyBadge extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  /** Background color of the badge. */
  @property({ type: String, reflect: true })
  statusColor: string = "success";

  /** Color of text content inside the badge. */
  @property({ type: String, reflect: true })
  textColor: string = "default";

  /** Color of the badge icon (when `isIcon` is true). */
  @property({ type: String, reflect: true })
  iconColor: string = "";

  /** Position of the badge relative to content. */
  @property({ type: String, reflect: true }) position: SkyBadgePosition = "default";

  /** Value or icon name displayed inside the badge. */
  @property({ type: String }) value: string = "";

  /** Whether the badge should render as an icon instead of text. */
  @property({ type: Boolean, reflect: true }) isIcon =
    false;

  /** Size (width/height) of the badge. e.g., `8px`, `1rem`. */
  @property({ type: String, reflect: true }) size: string = "";

  /** Shape of the badge. `circle`, `pill`, or `custom` (use `--sky-badge-radius`). */
  @property({ type: String, reflect: true }) shape: SkyBadgeShape = "custom";

  /** Maximum numeric count before showing e.g., `99+`. Ignored in icon mode. */
  @property({ type: Number }) max = 99;

  /** Do not render the badge if numeric value is `0`. */
  @property({ type: Boolean }) hideWhenZero =
    false;

  /** Controls visibility regardless of value (useful for async data). */
  @property({ type: Boolean, reflect: true }) show = true;

  /** Pixel or CSS length offsets for `default`/`floating` positions. */
  @property({ type: String }) offsetX: string = "0px";
  @property({ type: String }) offsetY: string = "0px";

  /** Optional outline ring around the badge. */
  @property({ type: Boolean }) ring = false;
  @property({ type: String }) ringColor: string =
    "primary";
  @property({ type: String }) ringOffset: string =
    "1px";

  /** Accessiblity: override the generated aria-label for screen readers. */
  @property({ type: String }) badgeLabel:
    | string
    | null = null;

  /** Accessibility: if true, hides the badge from assistive tech (purely decorative). */
  @property({ type: Boolean })
  badgeAriaHidden = false;

  /** Make badge focusable/clickable and emit `badge-click`. */
  @property({ type: Boolean }) interactive = false;

  /** Corner preset; use "custom" to rely on offsetX/offsetY and existing behavior. */
  @property({ type: String, reflect: true }) placement: SkyBadgePlacement = "custom";

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  @query(".badge") private _badgeEl!: HTMLSpanElement;

  /** Ref for the badge element (e.g. for tooltip anchor or positioning). */
  public badgeRef = createRef<HTMLSpanElement>();

  static override shadowRootOptions: ShadowRootInit = {
    mode: "open",
    delegatesFocus: true,
  };

  static override styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: inline-block;
      position: relative;
      box-sizing: border-box;
      /* Theming: override with --sky-badge-* for custom sizing */
      --sky-badge-radius: var(--sky-radius-secondary);
      --sky-badge-padding: var(--sky-space-0\\.5, 0.125rem);
      --sky-badge-font-size: var(--sky-font-size-xs, 0.75rem);
      --sky-badge-font-weight: var(--sky-font-weight-bold, 700);
      --sky-badge-shadow: none;
      --sky-badge-translate-x: 0px;
      --sky-badge-translate-y: 0px;
    }

    .container {
      display: inline-flex;
      position: relative;
      align-items: center;
    }

    /* Badge base */
    .badge {
      min-height: 5px;
      padding: var(--sky-badge-padding);
      display: inline-flex;
      justify-content: center;
      align-items: center;
      box-shadow: var(--sky-badge-shadow);
      color: var(--_badge-text-color);
      background-color: var(--_badge-bg);
      border: var(--_badge-border, none);
      font: var(--sky-text-caption);
      font-weight: var(--sky-badge-font-weight);
      font-size: var(--sky-badge-font-size);
      line-height: var(--sky-line-height-none, 1);
      letter-spacing: var(--sky-letter-spacing-tight, -0.01em);
      position: absolute;
      z-index: 1;
      border-radius: var(--_badge-radius);
      min-width: var(--_badge-size);
      min-height: var(--_badge-size);
      top: var(--_anchor-top, auto);
      right: var(--_anchor-right, auto);
      bottom: var(--_anchor-bottom, auto);
      left: var(--_anchor-left, auto);
      transform: translate(
        var(--sky-badge-translate-x),
        var(--sky-badge-translate-y)
      );
      user-select: none;
      -webkit-user-select: none;
      white-space: nowrap;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition: var(--sky-transition-fast);
    }

    @media (prefers-reduced-motion: reduce) {
      .badge {
        transition: none;
      }
    }

    /* Floating offsets & overlap */
    .badge.floating {
      transform: translate(
        calc(50% + var(--sky-badge-translate-x)),
        calc(-50% + var(--sky-badge-translate-y))
      );
    }

    /* Inline style participates in text flow */
    .badge.inline {
      position: static;
      display: inline-flex;
      margin-inline-start: var(--sky-space-1\\.5, 6px);
      transform: translate(
        var(--sky-badge-translate-x),
        var(--sky-badge-translate-y)
      );
    }

    /* Icon styling */
    sky-icon[part="icon"] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      font-size: var(--_icon-size);
      color: var(--_icon-color);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    /* Interactive affordances */
    .badge[role="button"] {
      cursor: pointer;
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }
    .badge[role="button"]:focus-visible {
      outline: none;
      box-shadow: var(--sky-focus-ring);
    }

    /* Ring (outline) */
    .badge.ringed::after {
      content: "";
      position: absolute;
      inset: calc(-1 * var(--_ring-offset));
      border-radius: inherit;
      box-shadow: 0 0 0 2px var(--_ring-color);
      pointer-events: none;
    }

    .badge[data-placement="top-center"] {
      transform: translate(
        calc(-50% + var(--sky-badge-translate-x)),
        var(--sky-badge-translate-y)
      );
    }

    /* Custom solid status fills (not soft semantic surfaces). */
    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ".badge[data-solid-contrast]",
          fillVar: "--_badge-bg",
        },
        {
          selector: '.badge[data-solid-contrast] sky-icon[part="icon"]',
          fillVar: "--_badge-bg",
        },
      ]),
    )}
  `,
  ];

  protected override updated(changed: PropertyValues<this>): void {
    if (changed.has("size")) {
      if (/^\d+$/.test(this.size as string)) this.size = `${this.size}px`;
    }
    if (
      changed.has("position") &&
      !SKY_BADGE_POSITIONS.includes(this.position)
    ) {
      this.position = "default";
    }
    if (changed.has("shape") && !SKY_BADGE_SHAPES.includes(this.shape)) {
      this.shape = "custom";
    }
    if (
      changed.has("placement") &&
      !SKY_BADGE_PLACEMENTS.includes(this.placement)
    ) {
      this.placement = "custom";
    }
  }

  /**
   * Forces badge visibility on.
   *
   * @returns {void}
   */
  public showBadge(): void {
    this.show = true;
  }

  /**
   * Forces badge visibility off.
   *
   * @returns {void}
   */
  public hideBadge(): void {
    this.show = false;
  }

  /**
   * Updates badge value for text/count or icon modes.
   *
   * @param value Badge value or icon name.
   * @returns {void}
   */
  public setValue(value: string): void {
    this.value = value;
  }

  /** Emits `badge-click` for interactive activation. */
  private _onActivate = (e: Event) => {
    if (!this.interactive) return;
    this.dispatchEvent(
      new CustomEvent<SkyBadgeClickEventDetail>("badge-click", {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    );
  };

  /** Handles keyboard activation with Enter and Space. */
  private _onKeydown = (e: KeyboardEvent) => {
    if (!this.interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this._onActivate(e);
    }
  };

  /** Computes accessible badge label from current render state. */
  private _computeAriaLabel(displayValue: string | null): string | null {
    if (this.badgeAriaHidden) return null;
    if (this.badgeLabel) return this.badgeLabel;
    if (this.isIcon) return ""; // purely visual when icon unless overridden
    if (displayValue == null || displayValue === "") return "Badge";
    const n = Number(displayValue);
    return isNaN(n) ? `Badge: ${displayValue}` : `Badge count: ${displayValue}`;
  }

  /** Resolves anchor edges from placement preset. */
  private _computeAnchors(placement: SkyBadgePlacement): {
    top: string;
    right: string;
    bottom: string;
    left: string;
  } {
    if (placement === "custom") {
      return { top: "0", right: "0", bottom: "auto", left: "auto" };
    }
    switch (placement) {
      case "top-right":
        return { top: "0", right: "0", bottom: "auto", left: "auto" };
      case "top-left":
        return { top: "0", right: "auto", bottom: "auto", left: "0" };
      case "bottom-left":
        return { top: "auto", right: "auto", bottom: "0", left: "0" };
      case "bottom-right":
        return { top: "auto", right: "0", bottom: "0", left: "auto" };
      case "top-center":
        return { top: "0", right: "auto", bottom: "auto", left: "50%" };
      default:
        return { top: "0", right: "0", bottom: "auto", left: "auto" };
    }
  }

  /** Maps semantic status tokens to soft badge surface colors. */
  private _resolveBadgeSurface(): {
    bg: string;
    color: string;
    border: string;
  } {
    const semanticKey = resolveSemanticSurfaceKey(this.statusColor);
    if (semanticKey) {
      const soft = semanticSoftSurfaceTokens(semanticKey, { bordered: true });
      const color =
        this.textColor && this.textColor !== "default"
          ? resolveColor(this.textColor)
          : soft.color;
      return { bg: soft.bg, color, border: soft.border };
    }

    return {
      bg: resolveColor(this.statusColor),
      color:
        this.textColor && this.textColor !== "default"
          ? resolveColor(this.textColor)
          : ON_COLOR_FALLBACK_LIGHT,
      border: "none",
    };
  }

  override render() {
    const numeric = Number(this.value);
    const isNumeric = !isNaN(numeric) && !this.isIcon;
    const shouldHideZero = this.hideWhenZero && isNumeric && numeric === 0;
    const visible = this.show && !shouldHideZero;
    const displayValue = this.isIcon
      ? null
      : isNumeric && this.max > 0 && numeric > this.max
        ? `${this.max}+`
        : this.value;

    const cls = ["badge", this.position];
    if (this.ring) cls.push("ringed");

    const computedRadius =
      this.shape === "circle"
        ? "var(--sky-radius-full)"
        : this.shape === "pill"
          ? "var(--sky-radius-full)"
          : "var(--sky-badge-radius)";

    const surface = this._resolveBadgeSurface();
    const solidContrast =
      !resolveSemanticSurfaceKey(this.statusColor) &&
      (!this.textColor || this.textColor === "default");
    const { top, right, bottom, left } = this._computeAnchors(this.placement);
    const badgeStyle = {
      "--_badge-bg": surface.bg,
      "--_badge-text-color": surface.color,
      "--_badge-border": surface.border,
      "--_badge-radius": computedRadius,
      "--_badge-size": this.size,
      "--_icon-size": this.size,
      "--_icon-color": this.iconColor
        ? resolveColor(this.iconColor)
        : this.textColor && this.textColor !== "default"
          ? resolveColor(this.textColor)
          : surface.color,
      "--_ring-color": resolveColor(this.ringColor),
      "--_ring-offset": this.ringOffset,
      "--sky-badge-translate-x": this.offsetX,
      "--sky-badge-translate-y": this.offsetY,
      "--_anchor-top": top,
      "--_anchor-right": right,
      "--_anchor-bottom": bottom,
      "--_anchor-left": left,
      "--sky-focus-ring-color": surface.bg,
    };

    const ariaLabel = this._computeAriaLabel(displayValue);

    return html`
      <div class="container" part="container">
        <slot></slot>
        ${when(
      visible,
      () => html`
            <span
              class=${cls.join(" ")}
              part="badge"
              style=${styleMap(badgeStyle)}
              role=${this.interactive ? "button" : "status"}
              aria-live=${this.interactive ? "off" : "polite"}
              data-placement=${this.placement}
              ?data-solid-contrast=${solidContrast}
              aria-atomic="true"
              aria-label=${ariaLabel ?? nothing}
              aria-hidden=${this.badgeAriaHidden ? "true" : "false"}
              tabindex=${this.interactive ? 0 : -1}
              @click=${this._onActivate}
              @keydown=${this._onKeydown}
              ${ref(this.badgeRef)}
            >
              <slot name="badge">
                ${when(
        this.isIcon,
        () =>
          html`<sky-icon part="icon" icon=${this.value} aria-hidden="true"></sky-icon>`,
        () => html`${displayValue}`,
      )}
              </slot>
            </span>
          `,
    )}
      </div>
    `;
  }
}
